import { motion, AnimatePresence } from 'motion/react';
import useWindowSize from '@/utils/useWindowSize';
import { getOrderStatusTime } from './getOrderStatusTime';
import { ChipOrder } from '@/ui/Chip-Order';
import { cn } from '@/utils/merge';
import { PlatformLogo } from '@/ui/Platform-Logo';
import { OrderMessageButton } from '@/components/communication/OrderMessageButton';
import s from './active-orders.module.css';
import { ASSIGNED_BY, PAYMENT_TYPES } from '@/utils/constants';
import { WoltBadge } from '@/components/Wolt-Badge';
import { pinAreaDiffers, pinAreaLabel } from '@/utils/pin-area';
import { colorZonesActive, zoneColorForOrder } from '@/utils/color-zones';

const ActiveOrders = ({
	mapVisible,
	data,
	showAreaPromLabel,
	showShop,
	// The shop charge is the shop's own bill, so it is not admin-only like the
	// rider fee — a shop sees it unless the city set hideShopFeesFromShops.
	showShopFee,
	// Whether clicking an order opens the order modal. Used to be showShop (i.e.
	// admin-only); a shop with allowChangeOrderDelay gets in too now.
	canOpenOrderModal,
	selectShopForDelivery,
	// openCompleteModal,
	// openCancelModal,
	openGenericOrderModal,
	openOrderDeliverySelectedModal,
	withDeliveryPhoneVisible,
	onOpenClient,
	continuousOrderDisplayTime,
	// The city whose colour zones paint the rows — admins only, null otherwise.
	colorZonesCity,
}) => {
	const size = useWindowSize();
	const isSmall = size.width <= 920;
	const zonesActive = colorZonesActive(colorZonesCity);

	const onClickDelivery = order => {
		if (!showShop) {
			return;
		}

		if (!order.delivery?.firstname) {
			selectShopForDelivery(order);
			return;
		}

		openOrderDeliverySelectedModal(order);
	};

	const orderClick = order => {
		if (!canOpenOrderModal) {
			return;
		}

		openGenericOrderModal(order.id);
	};

	// <ActiveOrdersMobileView
	//             order={order}
	//             idx={idx}
	//             showShop={showShop}
	//             openCompleteModal={openCompleteModal}
	//             orderClick={orderClick}
	//             onOpenClient={onOpenClient}
	//             onClickDelivery={onClickDelivery}
	//             bigColumns={bigColumns}
	//             showAreaPromLabel={showAreaPromLabel}
	//             withDeliveryPhoneVisible={withDeliveryPhoneVisible}
	//           />
	// console.log(data?.map(it => ({ shop: it.shop.name, id: it.id })));
	return (
		<div
			className={showShop ? s.container : s.containerShop}
			style={!mapVisible ? { maxWidth: 2500 } : undefined}
		>
			{/* {data?.length > 0 ? (
				<div className='mb-2 text-lg font-semibold text-cyan-700!'>
					{data?.length} παραγγελί{data?.length === 1 ? 'α' : 'ες'}
				</div>
			) : (
				<div />
			)} */}
			<AnimatePresence>
				{data?.map?.((order, idx) => {
					const { orderStatusTime, preparationStatus } = getOrderStatusTime({
						order,
						continuousOrderDisplayTime,
					});

					// Shops get the wall-clock time the order was created, same as on drafts.
					const time = new Date(order?.createdAt).toLocaleTimeString([], {
						hour: '2-digit',
						minute: '2-digit',
						hour12: false,
					});

					// The colour zone the order's pin is in: a thin line on the right edge
					// of the address column, which sits at the same place in every row, so
					// the colours line up one under the other. Drawn as an inset shadow,
					// which takes no room; the small padding is there in every row of a
					// city with zones (coloured or not), so no row is laid out differently.
					// In such a city the three columns also get min-w-0, so a long shop or
					// rider name wraps inside its column instead of pushing the address
					// column (and its line) a few pixels sideways.
					const zoneColor = zoneColorForOrder(order, colorZonesCity);
					// And a hairline underline in the same colour, under the area (under
					// the street when the order has no area).
					const zoneUnderline = zoneColor
						? {
								textDecorationLine: 'underline',
								textDecorationColor: zoneColor,
								textDecorationThickness: 1,
								textUnderlineOffset: 3,
							}
						: undefined;

					let borderLeftColor;
					let hover;

					if (order.deliveredBy === 'wolt') {
						borderLeftColor = 'rgba(26,193,230)';
						// borderLeftColor = 'rgba(76,175,80,1)';
						hover = 'hover:bg-[rgba(26,193,230,0.1)]';
					} else {
						switch (orderStatusTime.className) {
							case 'bgDelayed':
								borderLeftColor = 'rgba(165,42,42,1)';
								// borderLeftColor = 'rgba(76,175,80,1)';
								hover = 'hover:bg-[rgba(165,42,42,0.1)]';
								// hover = 'hover:bg-[rgba(76,175,80,0.1)]';
								break;

							case 'bgPending':
								borderLeftColor = 'rgba(255,102,0,1)';
								hover = 'hover:bg-[rgba(255,102,0,0.1)]';

								break;

							case 'bgToPick':
								borderLeftColor = 'rgba(126,29,196,1)';
								hover = 'hover:bg-[rgba(126,29,196,0.1)]';

								break;

							case 'bgPicked':
								borderLeftColor = 'rgba(100,149,237,1)';
								hover = 'hover:bg-[rgba(100,149,237,0.1)]';

								break;

							default:
								borderLeftColor = 'rgba(0,0,0,0.5)';
								break;
						}
					}

					return (
						// console.log("eee", order.price, order.price.length) ||
						<motion.div
							key={order.id}
							layout // <<< THIS DOES THE MAGIC
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{
								opacity: 0,
								y: -20,
								backgroundColor: '#00ff00', // green flash
							}}
							transition={{ type: 'spring', stiffness: 270, damping: 23 }}
							// auto einai to glow:
							//  ${s[`orderRowGlow${orderStatusTime.className}`]}
							className={`${s.order} ${s.orderRowGlow} will-change-transform ${hover}`}
							style={{ borderLeftColor }}
						>
							<div className={`${s.orderRow} pr-1`}>
								{!isSmall && (
									<div
										className='mr-2 flex h-[27px] w-[27px] items-center justify-center rounded-full text-white!'
										onClick={() => canOpenOrderModal && orderClick(order)}
										style={{
											backgroundColor: borderLeftColor,
											cursor: canOpenOrderModal ? 'pointer' : 'default',
										}}
									>
										{idx + 1}
									</div>
								)}
								<div
									className={cn(
										// 'mr-0.5 flex gap-y-1.5',
										// showShop ? 'flex flex-[0.83] flex-col' : 'flex-row items-center gap-x-2',
										'mr-0.5 flex flex-[0.83] flex-col gap-y-1.5',
										zonesActive && 'min-w-0',

										'min-[860px]:flex-[0.7]',
										'min-[1200px]:flex-[0.6]',
										'min-[1300px]:flex-[0.5]',
									)}
								>
									<div
										className={cn(`py-1 leading-4 font-semibold`, 'min-[1020px]:leading-5')}
										onClick={() => canOpenOrderModal && orderClick(order)}
										style={{
											// backgroundColor: borderLeftColor,
											cursor: canOpenOrderModal ? 'pointer' : 'default',
										}}
									>
										{showShop ? order.shop?.name : null}

										<div className='flex flex-row gap-x-1'>
											<div
												className='font-regular !text-slate-600'
												style={{ fontSize: showShop ? 14 : 16 }}
											>
												{order?.platformHashOrderId ? `#${order?.platformHashOrderId}` : ''}
											</div>

											{!showShop && <PlatformLogo platform={order.platform || 'efood'} />}
											<OrderMessageButton orderId={order.id} />
										</div>
									</div>
									{order?.price || order.distanceMeters || order.fee || order.shopFee ? (
										<div className='mb-0.5 flex flex-row gap-x-1.5'>
											{order?.price || order.paymentType === PAYMENT_TYPES.CARD ? (
												<ChipOrder
													value={order?.price || '-'}
													variant='price'
													isCard={order.paymentType === PAYMENT_TYPES.CARD}
												/>
											) : null}
											{order?.tip ? <ChipOrder value={order?.tip} variant='tip' /> : null}
											{order.fee && showShop ? <ChipOrder value={order.fee} variant='fee' /> : null}
											{order.shopFee && showShopFee ? (
												<ChipOrder value={order.shopFee} variant='shopFee' />
											) : null}
											{order.distanceMeters ? (
												<ChipOrder
													value={(order.distanceMeters / 1000)?.toFixed(1)}
													// value={700 / 1000}
													variant='distance'
												/>
											) : null}
										</div>
									) : null}
								</div>

								<div
									className={cn(
										'ml-1 flex flex-[1] flex-col min-[900px]:flex-row min-[900px]:gap-x-2 min-[1020px]:flex-[1.2]',
										zonesActive && 'min-w-0',
										'min-[1400px]:flex-[1.4]',
										'max-[600px]:flex-[1.1]',
									)}
								>
									<div
										onClick={
											order.client?.id && showShop
												? () => onOpenClient(order.client, order.clientId)
												: undefined
										}
										className={cn(
											order.client?.id && 'font-semibold',
											'min-[900px]:flex-[1.1]',
											'min-[1200px]:flex-[0.65]',
											'min-[1500px]:flex-[0.6]',
											// 'min-[1200px]:[flex-shrink:0]', // ← here, on the client div itself
											//
										)}
										style={{ cursor: order.client?.f && showShop ? 'pointer' : 'default' }}
									>
										{order?.client?.f}
									</div>

									<div
										className={cn(
											'flex min-w-0 flex-col',
											'min-[900px]:flex-1',
											'min-[1200px]:flex-[1.15]',
											//
										)}
										style={
											zonesActive
												? {
														paddingRight: 12,
														boxShadow: zoneColor ? `inset -6px 0 0 ${zoneColor}` : undefined,
													}
												: undefined
										}
									>
										{order.address?.s || order.address?.street ? (
											<div
												className='leading-4 font-semibold min-[900px]:leading-5'
												style={zoneColor && !order.area ? zoneUnderline : undefined}
											>
												{order.address?.s || order.address?.street}
											</div>
										) : null}

										{order.area ? (
											<div className='' style={zoneColor ? zoneUnderline : undefined}>
												{order.area}
											</div>
										) : null}

										{/* Admins only (showShop). */}
										{showShop && pinAreaDiffers(order) ? (
											<div className='text-[12px] leading-4 text-orange-600'>
												{pinAreaLabel(order)}
											</div>
										) : null}
									</div>
								</div>

								<div
									className={cn(
										'flex flex-[1] flex-col-reverse items-end',
										zonesActive && 'min-w-0',
										// 'min-[860px]:flex-row',
										'max-[700px]:flex-[0.87]',
										'min-[700px]:max-[860px]:flex-[0.7]',
										'min-[920px]:flex-[0.85]',
										'min-[860px]:justify-end min-[860px]:gap-x-2',
										'min-[1020px]:flex-col-reverse',
										'min-[1300px]:flex-[0.6]',
										'min-[1500px]:flex-[0.74] min-[1500px]:flex-row min-[1500px]:items-center',
									)}
								>
									{order.deliveredBy === 'wolt' ? (
										<>
											{/* <img
												src={'/images/platform-logos/wolt_drive.png'}
												className='h-[19.6px] w-[60px]'
											/> */}
											{/* TODO
											TODO
											TODO
											TODO
											TODO
											TODO
											TODO
											TODO
											TODO
											TODO */}
											{
												<WoltBadge
													woltMeta={order.wolt_meta}
													onClick={() => onClickDelivery(order)}
												/>
											}
											{/* <WoltBadge woltMeta={order.wolt_meta} /> */}
										</>
									) : (
										<>
											<div
												onClick={() => onClickDelivery(order)}
												className={cn(
													`rounded-sm border-1 px-1.5 py-0.5 whitespace-nowrap ${
														order.delivery?.firstname ? '' : s.selectDelivery
													} ${
														showShop &&
														(order.adminAssigned
															? !order.assignConfirmed
															: !order.delivery?.firstname) &&
														s.pointer
													} ${
														!showShop
															? ''
															: order.adminAssigned
																? order.assignConfirmed
																	? s.assignConfirmed
																	: s.assignNotConfirmed
																: ''
													} `,
													// Two facts, two channels. The TEXT colour keeps saying whether the
													// driver has acknowledged (orange/green, untouched). The BACKGROUND
													// says who put them on it: the system leaves a teal chip, a person
													// or a self-pick leaves the plain grey one it always had.
													order.assignedBy === ASSIGNED_BY.AUTO
														? 'border-teal-500/45 bg-teal-500/12'
														: 'border-zinc-300/90 bg-zinc-300/30',
													'tracking-tight',
													`${order.delivery?.firstname} ${order.delivery?.lastname}`?.length > 19 &&
														'text-[0.94em]!',
												)}
												style={{
													// The driver chip assigns a driver, which stays admin-only.
													cursor: showShop ? 'pointer' : 'default',
													textAlign: isSmall ? 'right' : 'left',
												}}
											>
												{order.delivery?.firstname
													? `${order.delivery?.firstname} ${order.delivery?.lastname} ${
															!showShop && withDeliveryPhoneVisible ? order.delivery?.phone : ''
														}`
													: !showShop
														? ''
														: 'Ανάθεση'}
											</div>

											<div
												className={`${s.minutes} ${s[orderStatusTime.className]}`}
												style={{ marginBottom: 1 }}
											>
												{preparationStatus === 'not-ready' ? 'σε ' : ''} {orderStatusTime.time}′
											</div>
										</>
									)}
								</div>
							</div>

							<div className='flex flex-row gap-x-2'>
								{!showShop && (
									<div className='font-regular! mt-0.5 mr-1 rounded-[10px] bg-slate-400! px-2.5 py-0.5 text-zinc-100!'>
										{time}
									</div>
								)}
								{order?.comments?.length > 0 && (
									<div className='font-regular! mt-0.5 mr-1 rounded-[4px] bg-pink-200! px-1.5 py-0.5 text-zinc-900!'>
										{order.comments}
									</div>
								)}
							</div>
						</motion.div>
					);
				})}
			</AnimatePresence>
		</div>
	);
};

export default ActiveOrders;
